import React, { useState, useMemo, useEffect, useRef } from 'react';
import { collection, onSnapshot, query, where, doc, updateDoc, serverTimestamp, orderBy, limit, arrayUnion, arrayRemove } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';

// ── Intent scoring ─────────────────────────────────────────────────────────────

// Matches a specific dollar/k amount in the comment
const DOLLAR_RX = /\$[\d,]+k?|\b\d{2,3}k\b/i;
// Matches buyer-ready language that, combined with a dollar amount, signals an actual offer
const OFFER_INTENT_RX = /\b(i[' ]?m|i\s+am)\s+in\b|\bready\b|\boffering\b|\boffer\b|\bi[' ]?ll\s+(do|pay|take|offer|close)\b|\bcash\s+(buyer|offer|ready)\b|\bwire\b|\blet[' ]?s\s+(do|close|make)\b/i;

const HOT_RX = /\bi[' ]?ll (take|buy|pay|offer|close|do)\b|\bcash buyer\b|\bwe buy\b|\bsend (me )?(the )?details?\b|\bmy (number|#)\b|\bcall me\b|\btext me\b|\b\d{3}[\s.\-]\d{3}[\s.\-]\d{4}\b|\bi'm in\b|\bready to (buy|close|move|purchase)\b|\bbuying\b|\bclosing\b|\barv\b/i;
const WARM_RX = /\binterested\b|\bpm (me|sent|you)\b|\bdm (me|sent)\b|\bsend (info|more|pics|details?|link|address)\b|\bwhat[' ]?s the (price|asking|address|deal)\b|\bstill available\b|\bwhere is (it|this)\b|\binbox me\b|\breach out\b|\bhow much\b|\bpm'?d\b|\bmessaged\b/i;
const NOISE_RX = /^(following|watching|nice|great|good luck|congrats|lol|wow|👍|❤|🙏|😂|thanks?|thank you|awesome|cool|ok|okay)\.?$/i;

function scoreIntent(comment) {
  const t = (comment || '').trim();
  // Offer tier: specific dollar amount + buyer-ready language
  if (DOLLAR_RX.test(t) && OFFER_INTENT_RX.test(t))
    return { tier: 'offer', score: 4, label: '💰 Offer', color: '#065f46', bg: '#ecfdf5', border: '#6ee7b7' };
  if (HOT_RX.test(t))   return { tier: 'hot',   score: 3, label: '🔥 Hot',   color: '#dc2626', bg: '#fff5f5', border: '#fecaca' };
  if (WARM_RX.test(t))  return { tier: 'warm',  score: 2, label: '👍 Warm',  color: '#b45309', bg: '#fffbeb', border: '#fde68a' };
  if (NOISE_RX.test(t)) return { tier: 'noise', score: 0, label: '📋 Noise', color: '#9ca3af', bg: '#f9fafb', border: '#e5e7eb' };
  return                        { tier: 'warm',  score: 1, label: '👍 Warm',  color: '#b45309', bg: '#fffbeb', border: '#fde68a' };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseDate(str) {
  if (!str) return null;
  try { return new Date(str.replace(' at ', ' ')); } catch { return null; }
}

function toYMD(date) { return date.toLocaleDateString('en-CA'); }
function startOfDay(d) { const x = new Date(d); x.setHours(0,0,0,0); return x; }

const PRESETS = [
  { label: 'All time',  key: 'all' },
  { label: 'Today',     key: 'today' },
  { label: 'Yesterday', key: 'yesterday' },
  { label: 'Last 7d',   key: '7d' },
  { label: 'Last 30d',  key: '30d' },
  { label: 'Custom',    key: 'custom' },
];

function getPresetRange(key) {
  const now = new Date();
  const sod = startOfDay(now);
  if (key === 'today') return { from: sod, to: now };
  if (key === 'yesterday') {
    const s = new Date(sod); s.setDate(s.getDate() - 1);
    const e = new Date(sod); e.setMilliseconds(-1);
    return { from: s, to: e };
  }
  if (key === '7d')  { const s = new Date(sod); s.setDate(s.getDate() - 6);  return { from: s, to: now }; }
  if (key === '30d') { const s = new Date(sod); s.setDate(s.getDate() - 29); return { from: s, to: now }; }
  return null;
}

// ── Avatar ────────────────────────────────────────────────────────────────────

function Avatar({ src, name, size = 38 }) {
  const [err, setErr] = useState(false);
  const initials = name ? name.split(' ').map(w => w[0]).slice(0, 2).join('') : '?';
  const palette = ['#6366f1','#ec4899','#f59e0b','#10b981','#3b82f6','#8b5cf6','#ef4444','#14b8a6'];
  const bg = palette[name ? name.charCodeAt(0) % palette.length : 0];
  if (!src || err) return (
    <div style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, background: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.35, fontWeight: 700, color: '#fff', letterSpacing: '-0.5px' }}>
      {initials}
    </div>
  );
  return <img src={src} alt={name} onError={() => setErr(true)}
    style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />;
}

// ── Mini Calendar ─────────────────────────────────────────────────────────────

function Calendar({ from, to, onChange }) {
  const [viewYear, setViewYear] = useState(() => new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => new Date().getMonth());
  const [picking, setPicking] = useState('from');
  const [hovered, setHovered] = useState(null);

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDow    = new Date(viewYear, viewMonth, 1).getDay();

  function prev() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }
  function next() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }
  function pick(day) {
    const d = startOfDay(new Date(viewYear, viewMonth, day));
    if (picking === 'from') { onChange({ from: d, to: null }); setPicking('to'); }
    else {
      const end = new Date(d); end.setHours(23,59,59,999);
      onChange(d < from ? { from: d, to: new Date(from).setHours(23,59,59,999) && from } : { from, to: end });
      setPicking('from');
    }
  }
  function inRange(day) {
    const d = new Date(viewYear, viewMonth, day);
    const hi = to || hovered;
    if (!from || !hi) return false;
    return d >= (from < hi ? from : hi) && d <= (from < hi ? hi : from);
  }

  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const DOW    = ['S','M','T','W','T','F','S'];

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
        <button onClick={prev} style={calNavBtn}>‹</button>
        <span style={{ fontSize:13, fontWeight:600, color:'#111' }}>{MONTHS[viewMonth]} {viewYear}</span>
        <button onClick={next} style={calNavBtn}>›</button>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2, marginBottom:4 }}>
        {DOW.map((d,i) => <div key={i} style={{ textAlign:'center', fontSize:10, color:'#c0c0c0', fontWeight:700, padding:'2px 0' }}>{d}</div>)}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2 }}>
        {Array.from({length:firstDow}).map((_,i) => <div key={`b${i}`}/>)}
        {Array.from({length:daysInMonth},(_,i)=>i+1).map(day => {
          const ymd = toYMD(new Date(viewYear, viewMonth, day));
          const isF = from && ymd === toYMD(from);
          const isT = to   && ymd === toYMD(to);
          const inR = inRange(day);
          return (
            <div key={day} onClick={() => pick(day)}
              onMouseEnter={() => picking==='to' && setHovered(new Date(viewYear,viewMonth,day))}
              onMouseLeave={() => setHovered(null)}
              style={{ textAlign:'center', padding:'5px 2px', fontSize:12, borderRadius:5, cursor:'pointer',
                fontWeight: isF||isT ? 700 : 400,
                background: isF||isT ? '#111' : inR ? '#f0f0f0' : 'transparent',
                color: isF||isT ? '#fff' : '#333' }}>
              {day}
            </div>
          );
        })}
      </div>
      <p style={{ margin:'10px 0 0', fontSize:11, color:'#bbb', textAlign:'center' }}>
        {picking === 'from' ? 'Select start date' : 'Select end date'}
      </p>
    </div>
  );
}

const calNavBtn = { background:'none', border:'none', cursor:'pointer', fontSize:18, color:'#999', padding:'0 4px', lineHeight:1 };

// ── Message Button ────────────────────────────────────────────────────────────

// Local Node server that handles DM drafts, AI scoring, and scrape orchestration.
// Override in deployed builds via REACT_APP_MSG_SERVER=https://your-ngrok-url.ngrok.app
const MSG_SERVER = process.env.REACT_APP_MSG_SERVER || 'http://localhost:3002';

function MessageButton({ profileUrl }) {
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');

  async function handleClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (status === 'loading') return;
    setStatus('loading');
    setErrMsg('');
    try {
      const res = await fetch(`${MSG_SERVER}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profileUrl }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || 'Unknown error');
      setStatus('done');
    } catch (err) {
      setStatus('error');
      setErrMsg(err.message.includes('fetch') ? 'Server offline' : err.message);
    }
  }

  if (status === 'done') return (
    <span style={{ fontSize:11, color:'#10b981', fontWeight:600, whiteSpace:'nowrap' }}>✓ Draft opened</span>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:2 }}>
      <button onClick={handleClick} disabled={status === 'loading'} style={{
        padding:'4px 10px', borderRadius:6, fontSize:11, fontWeight:600,
        cursor: status === 'loading' ? 'default' : 'pointer',
        border:'1.5px solid #d0d0d0',
        background: status === 'loading' ? '#f5f5f5' : '#fff',
        color: status === 'loading' ? '#bbb' : '#555',
        whiteSpace:'nowrap', transition:'all 0.1s',
      }}
        onMouseEnter={e => { if (status !== 'loading') e.target.style.borderColor = '#111'; }}
        onMouseLeave={e => { e.target.style.borderColor = '#d0d0d0'; }}
      >
        {status === 'loading' ? 'Opening…' : '✉ Message'}
      </button>
      {status === 'error' && <span style={{ fontSize:10, color:'#ef4444' }}>{errMsg}</span>}
    </div>
  );
}

// ── "Mark as DMed" button ───────────────────────────────────────────────────
// Tracks per-person outreach so we never message the same lead twice. `dmed`
// reflects person-level state (any comment by this person marked DMed).
function DmedButton({ dmed, onToggle }) {
  const [busy, setBusy] = useState(false);
  async function handle(e) {
    e.preventDefault(); e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try { await onToggle(); } catch (_) {} finally { setBusy(false); }
  }
  return (
    <button onClick={handle} disabled={busy}
      title={dmed ? 'Already DMed — click to unmark' : 'Mark this person as DMed'}
      style={{
        padding:'4px 10px', borderRadius:6, fontSize:11, fontWeight:700,
        cursor: busy ? 'default' : 'pointer', whiteSpace:'nowrap', transition:'all 0.1s',
        border: dmed ? '1.5px solid #10b981' : '1.5px solid #d0d0d0',
        background: dmed ? '#10b981' : '#fff',
        color: dmed ? '#fff' : '#555',
        opacity: busy ? 0.6 : 1,
      }}
      onMouseEnter={e => { if (!busy && !dmed) e.currentTarget.style.borderColor = '#10b981'; }}
      onMouseLeave={e => { if (!dmed) e.currentTarget.style.borderColor = '#d0d0d0'; }}
    >
      {busy ? '…' : dmed ? '✓ DMed' : '✓ Mark DMed'}
    </button>
  );
}

// ── Intent Badge ──────────────────────────────────────────────────────────────

function IntentBadge({ intent, aiReason, isAI }) {
  const [hover, setHover] = useState(false);
  return (
    <div style={{ position:'relative', display:'inline-flex', alignItems:'center' }}>
      <span
        onMouseEnter={() => aiReason && setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:20,
          color: intent.color, background: intent.bg,
          border:`1px solid ${intent.border}`,
          whiteSpace:'nowrap', cursor: aiReason ? 'help' : 'default',
          display:'flex', alignItems:'center', gap:3,
        }}
      >
        {intent.label}
        {isAI && <span style={{ fontSize:8, opacity:0.7, fontWeight:400 }}>AI</span>}
      </span>
      {hover && aiReason && (
        <div style={{
          position:'absolute', bottom:'calc(100% + 6px)', right:0, zIndex:300,
          background:'#1a1a1a', color:'#f0f0f0', fontSize:11, padding:'7px 10px',
          borderRadius:7, maxWidth:240, lineHeight:1.5, whiteSpace:'normal',
          boxShadow:'0 4px 16px rgba(0,0,0,0.25)',
        }}>
          {aiReason}
        </div>
      )}
    </div>
  );
}

// ── Comment Card ──────────────────────────────────────────────────────────────

function CommentCard({ entry, intent, aiReason, isAI, personDmed, onToggleDmed }) {
  const d = parseDate(entry.commentTime);
  const dateStr = d ? d.toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }) : '';
  const timeStr = d ? d.toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit' }) : '';

  // Green tint when this person has already been DMed (don't message twice).
  const baseBg = personDmed ? '#f0fdf4' : 'transparent';
  const hoverBg = personDmed ? '#e6fbee' : '#fafafa';

  // Link to the actual FB post: prefer the exact-comment deep link, then the post
  // permalink, then (for older comments scraped before we captured links) the group.
  const hasPostLink = !!(entry.commentUrl || entry.postUrl);
  const postLink = entry.commentUrl || entry.postUrl
    || (entry.groupId ? `https://www.facebook.com/groups/${entry.groupId}` : null);

  return (
    <div style={{
      display:'flex', gap:14, padding:'16px 20px',
      borderBottom:'1px solid #f3f3f3',
      borderLeft: personDmed ? '3px solid #10b981'
        : intent.tier === 'offer' ? '3px solid #6ee7b7' : intent.tier === 'hot' ? '3px solid #fca5a5' : intent.tier === 'warm' ? '3px solid #fde68a' : '3px solid transparent',
      background: baseBg,
      transition:'background 0.12s',
    }}
      onMouseEnter={e => e.currentTarget.style.background=hoverBg}
      onMouseLeave={e => e.currentTarget.style.background=baseBg}
    >
      <a href={entry.profileUrl} target="_blank" rel="noopener noreferrer" style={{ flexShrink:0, marginTop:2 }}>
        <Avatar src={entry.profilePhoto} name={entry.name} size={40} />
      </a>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:5, gap:8, flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, minWidth:0 }}>
            <a href={entry.profileUrl} target="_blank" rel="noopener noreferrer"
              style={{ fontWeight:600, fontSize:14, color:'#0f0f0f', textDecoration:'none', whiteSpace:'nowrap' }}>
              {entry.name}
            </a>
            <IntentBadge intent={intent} aiReason={aiReason} isAI={isAI} />
            {personDmed && (
              <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:20,
                color:'#065f46', background:'#d1fae5', border:'1px solid #6ee7b7', whiteSpace:'nowrap' }}>
                ✓ DMed
              </span>
            )}
            {entry.groupName && (
              <span style={{ fontSize:11, color:'#bbb', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:180 }}>
                {entry.groupName}
              </span>
            )}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
            <span style={{ fontSize:11, color:'#ccc' }}>{timeStr} · {dateStr}</span>
            {hasPostLink && (
              <a href={postLink} target="_blank" rel="noopener noreferrer"
                title="Open this post on Facebook"
                style={{ fontSize:11, fontWeight:600, color:'#6366f1', textDecoration:'none', whiteSpace:'nowrap' }}>
                ↗ Post
              </a>
            )}
            <MessageButton profileUrl={entry.profileUrl} />
            <DmedButton dmed={personDmed} onToggle={() => onToggleDmed(entry)} />
          </div>
        </div>
        {postLink ? (
          <a href={postLink} target="_blank" rel="noopener noreferrer"
            title={hasPostLink ? 'Open this post on Facebook' : 'Open the group on Facebook'}
            style={{ display:'block', margin:0, fontSize:13.5, color:'#3a3a3a', lineHeight:1.6, textDecoration:'none' }}
            onMouseEnter={e => { e.currentTarget.style.textDecoration='underline'; e.currentTarget.style.color='#1f1f1f'; }}
            onMouseLeave={e => { e.currentTarget.style.textDecoration='none'; e.currentTarget.style.color='#3a3a3a'; }}>
            {entry.comment}
          </a>
        ) : (
          <p style={{ margin:0, fontSize:13.5, color:'#3a3a3a', lineHeight:1.6 }}>{entry.comment}</p>
        )}
      </div>
    </div>
  );
}

// ── Sidebar Group Item ────────────────────────────────────────────────────────

function GroupItem({ name, count, checked, onChange }) {
  return (
    <label style={{ display:'flex', alignItems:'center', gap:9, padding:'6px 12px', cursor:'pointer',
      borderRadius:6, transition:'background 0.1s', userSelect:'none' }}
      onMouseEnter={e => e.currentTarget.style.background='#f5f5f5'}
      onMouseLeave={e => e.currentTarget.style.background='transparent'}
    >
      <div onClick={onChange} style={{
        width:16, height:16, borderRadius:4, flexShrink:0,
        border: checked ? '2px solid #111' : '1.5px solid #d0d0d0',
        background: checked ? '#111' : '#fff',
        display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.1s',
      }}>
        {checked && <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
          <path d="M1 3.5L3.5 6L8 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>}
      </div>
      <span style={{ fontSize:13, color:'#333', flex:1, lineHeight:1.3 }}>{name}</span>
      <span style={{ fontSize:11, color:'#bbb', fontVariantNumeric:'tabular-nums' }}>{count}</span>
    </label>
  );
}

// ── AI Analyze Button ─────────────────────────────────────────────────────────

function AIAnalyzeButton({ comments, onResults }) {
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [errMsg, setErrMsg] = useState('');

  async function handleClick() {
    if (status === 'loading' || !comments.length) return;
    setStatus('loading');
    setErrMsg('');
    try {
      // Send up to 120 comments to your local server's /ai-score endpoint.
      // Expected response: { results: [{ id, tier: 'hot'|'warm'|'noise', reason }] }
      const batch = comments.slice(0, 120).map(c => ({
        id: c.id,
        name: c.name || '',
        comment: c.comment || '',
      }));
      const res = await fetch(`${MSG_SERVER}/ai-score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comments: batch }),
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const data = await res.json();
      if (!Array.isArray(data.results)) throw new Error('Bad response format');
      onResults(data.results);
      setStatus('done');
    } catch (err) {
      setStatus('error');
      setErrMsg(err.message.includes('fetch') ? 'Server offline' : err.message);
      setTimeout(() => setStatus('idle'), 4000);
    }
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:2 }}>
      <button onClick={handleClick} disabled={status === 'loading'} style={{
        padding:'7px 14px', borderRadius:7, fontSize:12, fontWeight:600,
        cursor: status === 'loading' ? 'default' : 'pointer',
        border:'none',
        background: status === 'done' ? '#dcfce7' : status === 'error' ? '#fee2e2' : 'linear-gradient(135deg,#6366f1,#8b5cf6)',
        color: status === 'done' ? '#15803d' : status === 'error' ? '#dc2626' : '#fff',
        whiteSpace:'nowrap', transition:'all 0.15s', display:'flex', alignItems:'center', gap:6,
        opacity: status === 'loading' ? 0.7 : 1,
      }}>
        {status === 'loading' && (
          <span style={{ display:'inline-block', width:10, height:10, border:'2px solid rgba(255,255,255,0.4)',
            borderTopColor:'#fff', borderRadius:'50%', animation:'spin 0.6s linear infinite' }} />
        )}
        {status === 'idle'    && '✦ AI Score'}
        {status === 'loading' && 'Analyzing…'}
        {status === 'done'    && '✓ Scored'}
        {status === 'error'   && '✕ Failed'}
      </button>
      {status === 'error' && <span style={{ fontSize:10, color:'#ef4444' }}>{errMsg}</span>}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Intent Filter Chips ───────────────────────────────────────────────────────

const INTENT_FILTERS = [
  { key: 'all',   label: 'All' },
  { key: 'offer', label: '💰 Offer' },
  { key: 'hot',   label: '🔥 Hot' },
  { key: 'warm',  label: '👍 Warm' },
  { key: 'noise', label: '📋 Noise' },
];

function IntentChips({ value, onChange, counts }) {
  return (
    <div style={{ display:'flex', gap:6, alignItems:'center' }}>
      {INTENT_FILTERS.map(f => {
        const active = value === f.key;
        const count = counts[f.key] ?? 0;
        return (
          <button key={f.key} onClick={() => onChange(f.key)} style={{
            padding:'5px 11px', borderRadius:20, fontSize:12, fontWeight: active ? 700 : 500,
            cursor:'pointer', border: active ? '1.5px solid #111' : '1.5px solid #e5e5e5',
            background: active ? '#111' : '#fff',
            color: active ? '#fff' : '#555',
            whiteSpace:'nowrap', transition:'all 0.1s',
            display:'flex', alignItems:'center', gap:5,
          }}>
            {f.label}
            <span style={{ fontSize:10, opacity:0.65, fontVariantNumeric:'tabular-nums' }}>{count}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── Run-Scrape Bar ────────────────────────────────────────────────────────────
// Drives the local messageServer.js scrape endpoints. Lets you pick "All groups"
// or a single group and watch live progress streamed via SSE.

function RunScrapeBar() {
  const [groups, setGroups]       = useState([]);
  const [selected, setSelected]   = useState(''); // '' = all groups
  const [job, setJob]             = useState(null); // { jobId, kind, label, running, exitCode }
  const [lines, setLines]         = useState([]);
  const [expanded, setExpanded]   = useState(false);
  const [serverOk, setServerOk]   = useState(null);
  const esRef = useRef(null);
  const logRef = useRef(null);

  // Load groups + initial server health on mount
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const ping = await fetch(`${MSG_SERVER}/ping`).then(r => r.json());
        if (!alive) return;
        setServerOk(!!ping.ok);
      } catch { if (alive) setServerOk(false); }
      try {
        const r = await fetch(`${MSG_SERVER}/groups`).then(r => r.json());
        if (!alive) return;
        setGroups(r.groups || []);
      } catch {}
      // Check if a job is already running (so we can reattach)
      try {
        const s = await fetch(`${MSG_SERVER}/scrape-status`).then(r => r.json());
        if (s.active && s.running) {
          attachStream();
        } else if (s.active) {
          setJob({ jobId: s.jobId, kind: s.kind, label: s.label, running: false, exitCode: s.exitCode });
          setLines(s.tail || []);
        }
      } catch {}
    })();
    return () => { alive = false; if (esRef.current) try { esRef.current.close(); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll log to bottom when new lines arrive
  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [lines]);

  function attachStream() {
    if (esRef.current) try { esRef.current.close(); } catch {}
    const es = new EventSource(`${MSG_SERVER}/scrape-stream`);
    esRef.current = es;
    es.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'snapshot') {
          setJob({ jobId: msg.jobId, kind: msg.kind, label: msg.label, running: msg.running, exitCode: msg.exitCode });
          setLines(msg.lines || []);
        } else if (msg.type === 'line') {
          setLines(prev => [...prev, msg.text]);
        } else if (msg.type === 'status') {
          setJob(j => j ? { ...j, running: msg.running, exitCode: msg.exitCode } : j);
          if (!msg.running) { try { es.close(); } catch {} }
        }
      } catch {}
    };
    es.onerror = () => { try { es.close(); } catch {} };
  }

  async function startScrape() {
    setLines([]); setExpanded(true);
    try {
      const body = selected ? { groupId: selected } : {};
      const r = await fetch(`${MSG_SERVER}/scrape-start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok || data.error) throw new Error(data.error || `HTTP ${r.status}`);
      setJob({ jobId: data.jobId, kind: data.kind, label: data.label, running: true, exitCode: null });
      setLines(data.lines || []);
      attachStream();
    } catch (err) {
      setLines([`❌ Failed to start: ${err.message}`]);
    }
  }

  async function stopScrape() {
    try { await fetch(`${MSG_SERVER}/scrape-stop`, { method: 'POST' }); } catch {}
  }

  const running = job?.running;

  return (
    <div style={{
      borderBottom: '1px solid #eaeaea', background: '#fff',
      padding: '12px 24px', position: 'sticky', top: 0, zIndex: 20,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#1f2937' }}>▶ Run Scrape:</span>

        <select
          value={selected}
          onChange={e => setSelected(e.target.value)}
          disabled={running}
          style={{
            padding: '6px 10px', fontSize: 12, borderRadius: 6,
            border: '1px solid #d1d5db', background: running ? '#f5f5f5' : '#fff',
            color: '#111', minWidth: 280, cursor: running ? 'not-allowed' : 'pointer',
          }}
        >
          <option value="">All groups ({groups.length})</option>
          {groups.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
        </select>

        {!running ? (
          <button onClick={startScrape} disabled={serverOk === false} style={{
            padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6,
            border: 'none', cursor: serverOk === false ? 'not-allowed' : 'pointer',
            background: serverOk === false ? '#e5e7eb' : 'linear-gradient(135deg,#6366f1,#8b5cf6)',
            color: serverOk === false ? '#9ca3af' : '#fff',
          }}>
            {selected ? '▶ Run this group' : '▶ Run all groups'}
          </button>
        ) : (
          <button onClick={stopScrape} style={{
            padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6,
            border: '1px solid #fecaca', cursor: 'pointer',
            background: '#fef2f2', color: '#b91c1c',
          }}>
            ■ Stop
          </button>
        )}

        {job && (
          <span style={{
            fontSize: 11, padding: '3px 8px', borderRadius: 10,
            background: running ? '#dbeafe' : (job.exitCode === 0 ? '#dcfce7' : '#fef2f2'),
            color: running ? '#1e40af' : (job.exitCode === 0 ? '#166534' : '#991b1b'),
            fontWeight: 600,
          }}>
            {running ? `🔄 Running: ${job.label}` : `${job.exitCode === 0 ? '✓' : '✕'} ${job.label}`}
          </span>
        )}

        <span style={{ flex: 1 }} />

        {serverOk === false && (
          <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 500 }}>
            ⚠ Local server offline ({MSG_SERVER})
          </span>
        )}

        {lines.length > 0 && (
          <button onClick={() => setExpanded(x => !x)} style={{
            padding: '4px 10px', fontSize: 11, borderRadius: 5,
            border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', color: '#555',
          }}>
            {expanded ? 'Hide log' : `Show log (${lines.length})`}
          </button>
        )}
      </div>

      {expanded && lines.length > 0 && (
        <div ref={logRef} style={{
          marginTop: 10, padding: 10, background: '#0b0f1a', color: '#cbd5e1',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: 11, lineHeight: 1.45, borderRadius: 6,
          maxHeight: 240, overflowY: 'auto', whiteSpace: 'pre-wrap',
        }}>
          {lines.map((l, i) => <div key={i}>{l}</div>)}
        </div>
      )}
    </div>
  );
}

// ── Property helpers + card (Express Home Buyers wholesale deals) ───────────────

const NEW_WINDOW_HOURS = 36; // a property is "NEW" if firstSeenAt is within this window

function tsToMs(ts) {
  if (!ts) return null;
  if (typeof ts === 'object' && typeof ts.seconds === 'number') return ts.seconds * 1000;
  if (typeof ts === 'object' && typeof ts.toMillis === 'function') return ts.toMillis();
  const d = new Date(ts); return isNaN(d) ? null : d.getTime();
}
function isNewProperty(p) {
  const ms = tsToMs(p.firstSeenAt);
  if (!ms) return false;
  return (Date.now() - ms) < NEW_WINDOW_HOURS * 3600 * 1000;
}
function money(n) {
  if (n === null || n === undefined || isNaN(n)) return '—';
  return '$' + Number(n).toLocaleString('en-US');
}
function spreadPct(p) {
  if (!p.arv || !p.wholesalePrice) return null;
  return Math.round(((p.arv - p.wholesalePrice) / p.arv) * 100);
}
function relTime(ts) {
  const ms = tsToMs(ts);
  if (!ms) return '';
  const mins = Math.round((Date.now() - ms) / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

// ── State / group-matching ─────────────────────────────────────────────────────
const STATE_NAMES = { VA: 'Virginia', MD: 'Maryland', DC: 'Washington DC', WV: 'West Virginia', TN: 'Tennessee' };

// The property's 2-letter state: stored field, else parsed from the address.
function propState(p) {
  if (p.state) return p.state;
  const m = String(p.name || p.streetAddress || '').match(/\b([A-Z]{2})\s+\d{5}(?:-\d{4})?\b/);
  return m ? m[1] : null;
}

// Which states does a Facebook group serve, inferred from its name?
// DMV is shorthand for DC + Maryland + Virginia, so it applies to every home.
function groupStatesFor(name) {
  const n = ' ' + String(name).toLowerCase() + ' ';
  const s = new Set();
  if (/\bdmv\b/.test(n)) { s.add('DC'); s.add('MD'); s.add('VA'); }
  if (/\bd\.?c\.?\b/.test(n) || /washington/.test(n)) s.add('DC');
  if (/\bmd\b/.test(n) || /maryland/.test(n) || /baltimore/.test(n)) s.add('MD');
  if (/\bva\b/.test(n) || /virginia/.test(n)) s.add('VA');
  if (/\bwv\b/.test(n) || /west virginia/.test(n)) s.add('WV');
  if (/\btn\b/.test(n) || /tennessee/.test(n)) s.add('TN');
  return s;
}

// Stable per-person key for DM de-duplication: prefer the FB profile URL (path
// only, no query/hash), fall back to a normalized name. Lets us recognize the
// same person across multiple comments/groups so we never DM them twice.
function personKey(c) {
  const url = (c.profileUrl || '').trim();
  if (url) {
    try {
      const u = new URL(url, 'https://www.facebook.com');
      const id = u.searchParams.get('id');
      if (id) return `id:${id}`;
      const path = u.pathname.replace(/\/+$/, '').toLowerCase();
      if (path && path !== '/') return `url:${path}`;
    } catch (_) {
      return `url:${url.toLowerCase()}`;
    }
  }
  const name = (c.name || '').trim().toLowerCase();
  return name ? `name:${name}` : `id:${c.id}`;
}

// Split all known groups into ones that serve this property's state vs. region-less.
function groupsForProperty(allGroups, p) {
  const st = propState(p);
  const matched = [], general = [];
  (allGroups || []).forEach(g => {
    const states = groupStatesFor(g);
    if (states.size === 0) general.push(g);
    else if (st && states.has(st)) matched.push(g);
  });
  return { matched, general, state: st };
}

// AI-writes a ready-to-paste Facebook caption for this listing (via the local
// messageServer → Claude) and drops it straight onto the clipboard.
function ListingCopyButtons({ p }) {
  const [cap, setCap]   = useState({ s: 'idle', m: '' });  // Copy Caption (no link)
  const [both, setBoth] = useState({ s: 'idle', m: '' });  // Copy Caption + Link
  const [linkDone, setLinkDone] = useState(false);          // Copy Link
  // A caption saved on the listing doc — generated once, reused on every copy.
  const cached = (p.caption || '').trim();
  const dealUrl = `https://deals.expresshomebuyers.com/?source=lead+feed&recordId=${p.sfId || p.id}`;
  const linkLine = `🔗 Full details & numbers: ${dealUrl}`;
  const busy = cap.s === 'loading' || both.s === 'loading';

  async function copyText(text) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // clipboard API can fail (insecure context / permissions) — fall back to execCommand
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.focus(); ta.select();
      try { document.execCommand('copy'); } finally { document.body.removeChild(ta); }
    }
  }

  // Caption text: reuse the saved one, or generate + save it. force=true regenerates.
  async function ensureCaption(force) {
    if (cached && !force) return cached;
    const call = httpsCallable(functions, 'generateListingCaption');
    const { data } = await call({ listing: p });
    const caption = data && data.caption;
    if (!caption) throw new Error('no caption returned');
    try { await updateDoc(doc(db, 'expressHomeDeals', p.id), { caption, captionAt: serverTimestamp() }); } catch { /* copy still works */ }
    return caption;
  }

  // withLink appends the deal link (for approval-required groups). force regenerates.
  async function doCopy(e, withLink, force, set) {
    e.stopPropagation();
    const instant = cached && !force; // no AI call needed
    if (!instant) set({ s: 'loading', m: '' });
    try {
      const caption = await ensureCaption(force);
      await copyText(withLink ? `${caption}\n\n${linkLine}` : caption);
      set({ s: 'done', m: withLink ? 'Caption + link copied!' : (force ? 'Regenerated & copied!' : 'Copied!') });
      setTimeout(() => set({ s: 'idle', m: '' }), 2800);
    } catch (err) {
      set({ s: 'error', m: (err.message || 'failed').slice(0, 34) });
      setTimeout(() => set({ s: 'idle', m: '' }), 5000);
    }
  }

  async function copyLink(e) {
    e.stopPropagation();
    await copyText(dealUrl);
    setLinkDone(true);
    setTimeout(() => setLinkDone(false), 2500);
  }

  const gradBg = (st) => st === 'error' ? '#fee2e2' : st === 'done' ? '#dcfce7' : 'linear-gradient(135deg,#6366f1,#8b5cf6)';
  const gradFg = (st) => st === 'error' ? '#b91c1c' : st === 'done' ? '#166534' : '#fff';
  const capLabel = cap.s === 'loading' ? '✍️  Writing…' : cap.s === 'done' ? `✓ ${cap.m}` : cap.s === 'error' ? `✕ ${cap.m}` : (cached ? '📋 Copy Caption' : '✨ Generate & Copy');
  const bothLabel = both.s === 'loading' ? '✍️  Writing…' : both.s === 'done' ? `✓ ${both.m}` : both.s === 'error' ? `✕ ${both.m}` : '📋 Copy Caption + Link';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} onClick={(e) => e.stopPropagation()}>
      {/* Post body, NO link — for instant-post groups (then use Copy Link as the 1st comment) */}
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={(e) => doCopy(e, false, false, setCap)} disabled={busy}
          title="Post body with NO link — for groups that post instantly. Then use Copy Link as the first comment."
          style={{ flex: 1, padding: '9px 12px', borderRadius: 9, border: 'none',
            cursor: busy ? 'default' : 'pointer', fontSize: 12.5, fontWeight: 700,
            background: gradBg(cap.s), color: gradFg(cap.s), opacity: busy ? 0.7 : 1 }}>
          {capLabel}
        </button>
        {cached && !busy && (
          <button onClick={(e) => doCopy(e, false, true, setCap)}
            title="Regenerate a fresh caption (replaces the saved one)"
            style={{ flexShrink: 0, width: 40, padding: '9px 0', borderRadius: 9, border: '1px solid #e5e7eb',
              cursor: 'pointer', fontSize: 14, fontWeight: 700, background: '#fff', color: '#6366f1' }}>
            ↻
          </button>
        )}
      </div>
      {/* The link, for the FIRST COMMENT — instant-post groups (keeps the link out of the post) */}
      <button onClick={copyLink}
        title="Paste as the FIRST COMMENT on your post — for groups that post instantly"
        style={{ width: '100%', padding: '8px 12px', borderRadius: 9,
          border: `1px solid ${linkDone ? '#6ee7b7' : '#e5e7eb'}`, cursor: 'pointer', fontSize: 12, fontWeight: 700,
          background: linkDone ? '#ecfdf5' : '#fafafa', color: linkDone ? '#166534' : '#374151' }}>
        {linkDone ? '✓ Link copied — paste as 1st comment' : '🔗 Copy Link (1st comment)'}
      </button>
      {/* All-in-one — for APPROVAL-required groups (can't comment until an admin approves the post) */}
      <button onClick={(e) => doCopy(e, true, false, setBoth)} disabled={busy}
        title="Caption WITH the link baked in — for groups that must APPROVE posts, where you can't add a comment until it's approved."
        style={{ width: '100%', padding: '8px 12px', borderRadius: 9,
          border: `1px solid ${both.s === 'done' ? '#6ee7b7' : both.s === 'error' ? '#fecaca' : '#ddd6fe'}`,
          cursor: busy ? 'default' : 'pointer', fontSize: 12, fontWeight: 700,
          background: both.s === 'done' ? '#ecfdf5' : both.s === 'error' ? '#fee2e2' : '#f5f3ff',
          color: both.s === 'done' ? '#166534' : both.s === 'error' ? '#b91c1c' : '#6d28d9', opacity: busy ? 0.7 : 1 }}>
        {bothLabel}
      </button>
    </div>
  );
}

function PropertyCard({ p, groups, groupIdByName, selected, onSelect }) {
  const [imgErr, setImgErr] = useState(false);
  const [showGroups, setShowGroups] = useState(false);
  const isNew = isNewProperty(p);
  const removed = p.status === 'removed';
  const spread = spreadPct(p);
  const st = propState(p);
  const { matched, general } = useMemo(() => groupsForProperty(groups, p), [groups, p]);

  // Deep link to the exact property on Express's dashboard. Needs both the address
  // filter (spaces as "+") and the Salesforce recordId — without recordId it just
  // opens their full list. recordId is stored as sfId (and is also the doc id).
  const recordId = p.sfId || p.id;
  const detailUrl = (() => {
    const params = new URLSearchParams();
    if (p.streetAddress) params.set('filter', p.streetAddress);
    if (recordId) params.set('recordId', recordId);
    const qs = params.toString();
    return `https://deals.expresshomebuyers.com/${qs ? `?${qs}` : ''}`;
  })();

  // Per-group "posted" tracking. Persisted as a string[] (postedGroups) on the
  // expressHomeDeals/{id} doc via arrayUnion/arrayRemove — atomic and dot/slash-safe
  // (group ids & names contain '.', '/', '(' which break Firestore dot-path updates).
  const groupKey  = (g) => (groupIdByName && groupIdByName[g]) || g;
  const postedSet = useMemo(() => new Set(p.postedGroups || []), [p.postedGroups]);
  const groupUrl  = (g) => {
    const id = groupIdByName && groupIdByName[g];
    return id
      ? `https://www.facebook.com/groups/${id}`
      : `https://www.facebook.com/search/groups/?q=${encodeURIComponent(g)}`;
  };
  const togglePosted = async (g) => {
    const key = groupKey(g);
    try {
      await updateDoc(doc(db, 'expressHomeDeals', p.id), {
        postedGroups: postedSet.has(key) ? arrayRemove(key) : arrayUnion(key),
      });
    } catch (e) { console.error('togglePosted failed', e); }
  };
  const postedCount = [...matched, ...general].filter(g => postedSet.has(groupKey(g))).length;

  const renderGroup = (g, tone) => {
    const posted = postedSet.has(groupKey(g));
    const isGeneral = tone === 'general';
    return (
      <div key={g} onClick={(e) => e.stopPropagation()}
        style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 11.5,
          color: isGeneral ? '#9ca3af' : '#374151',
          background: posted ? '#ecfdf5' : (isGeneral ? '#fafafa' : '#f5f3ff'),
          border: `1px solid ${posted ? '#6ee7b7' : (isGeneral ? '#f0f0f0' : '#ede9fe')}`,
          borderRadius: 6, padding: '5px 8px', lineHeight: 1.3 }}>
        <input type="checkbox" checked={posted}
          onClick={(e) => e.stopPropagation()}
          onChange={() => togglePosted(g)}
          title={posted ? 'Posted — click to unmark' : 'Mark as posted to this group'}
          style={{ cursor: 'pointer', accentColor: '#10b981', flex: '0 0 auto', marginTop: 1 }} />
        <a href={groupUrl(g)} target="_blank" rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          title={`Open "${g}" on Facebook`}
          style={{ color: 'inherit', textDecoration: 'underline', textDecorationColor: '#cbd5e1',
            flex: 1, wordBreak: 'break-word' }}>
          {g}
        </a>
      </div>
    );
  };

  return (
    <div
      onClick={() => onSelect && onSelect(p)}
      title={onSelect ? (selected ? 'Click to clear the feed filter' : 'Click to filter the feed to this property’s groups') : undefined}
      style={{
        border: selected ? '2px solid #6366f1' : isNew ? '1.5px solid #34d399' : '1px solid #ececec',
        borderRadius: 14, overflow: 'hidden', background: '#fff',
        boxShadow: selected ? '0 0 0 3px rgba(99,102,241,0.20)' : isNew ? '0 4px 18px rgba(52,211,153,0.18)' : '0 1px 3px rgba(0,0,0,0.05)',
        display: 'flex', flexDirection: 'column', opacity: removed ? 0.55 : 1, position: 'relative',
        cursor: onSelect ? 'pointer' : 'default', transition: 'box-shadow 0.12s, border-color 0.12s',
    }}>
      {/* Image */}
      <div style={{ position: 'relative', width: '100%', aspectRatio: '16/10', background: '#f3f4f6' }}>
        {p.imageUrl && !imgErr ? (
          <img src={p.imageUrl} alt={p.streetAddress || 'property'} onError={() => setImgErr(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center',
            justifyContent: 'center', color: '#cbd5e1', fontSize: 13 }}>No image</div>
        )}
        {/* badges */}
        <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {selected && (
            <span style={{ background: '#6366f1', color: '#fff', fontSize: 11, fontWeight: 800,
              padding: '4px 9px', borderRadius: 999, boxShadow: '0 2px 6px rgba(99,102,241,0.45)' }}>✓ FILTERING</span>
          )}
          {isNew && !removed && (
            <span style={{ background: '#10b981', color: '#fff', fontSize: 11, fontWeight: 800,
              padding: '4px 9px', borderRadius: 999, letterSpacing: '0.04em',
              boxShadow: '0 2px 6px rgba(16,185,129,0.45)' }}>NEW</span>
          )}
          {removed && (
            <span style={{ background: '#6b7280', color: '#fff', fontSize: 11, fontWeight: 700,
              padding: '4px 9px', borderRadius: 999 }}>GONE</span>
          )}
          {p.propertyType && (
            <span style={{ background: 'rgba(17,17,17,0.78)', color: '#fff', fontSize: 10, fontWeight: 600,
              padding: '4px 9px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
              {p.propertyType}
            </span>
          )}
        </div>
        {spread !== null && (
          <span style={{ position: 'absolute', top: 10, right: 10, background: '#111', color: '#fff',
            fontSize: 11, fontWeight: 700, padding: '4px 9px', borderRadius: 999 }}>
            {spread}% spread
          </span>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: '13px 15px 15px', display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ fontSize: 21, fontWeight: 800, color: '#111', letterSpacing: '-0.5px' }}>
            {money(p.wholesalePrice)}
          </span>
          <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>ARV {money(p.arv)}</span>
        </div>

        <div style={{ display: 'flex', gap: 12, fontSize: 12.5, color: '#444', fontWeight: 600 }}>
          <span>{p.bedrooms ?? '—'} bd</span>
          <span style={{ color: '#ddd' }}>|</span>
          <span>{p.bathsTotal ?? '—'} ba</span>
          <span style={{ color: '#ddd' }}>|</span>
          <span>{p.squareFeet ? p.squareFeet.toLocaleString() : '—'} sqft</span>
          {p.yearBuilt ? (<><span style={{ color: '#ddd' }}>|</span><span>{p.yearBuilt}</span></>) : null}
        </div>

        <div style={{ fontSize: 13, color: '#222', lineHeight: 1.35 }}>
          {p.streetAddress || p.name}
          {(p.city || st) && (
            <span style={{ color: '#888' }}>, {[p.city, st].filter(Boolean).join(', ')}</span>
          )}
        </div>

        {/* Which FB groups this property can go into, by state */}
        <div style={{ borderTop: '1px solid #f2f2f2', paddingTop: 8 }}>
          <button onClick={(e) => { e.stopPropagation(); setShowGroups(s => !s); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600,
              color: matched.length ? '#6366f1' : '#9ca3af' }}>
            <span>{showGroups ? '▾' : '▸'}</span>
            {matched.length} group{matched.length !== 1 ? 's' : ''}
            {st ? ` for ${st}` : ' (no state)'}
            {postedCount > 0 ? ` · ${postedCount} posted` : ''}
          </button>
          {showGroups && (
            <div onClick={(e) => e.stopPropagation()}
              style={{ marginTop: 7, maxHeight: 230, overflowY: 'auto', display: 'flex',
              flexDirection: 'column', gap: 4 }}>
              <div style={{ fontSize: 10, color: '#b6b6b6', lineHeight: 1.35, marginBottom: 2 }}>
                Tap a name to open it on Facebook · check the box once you’ve posted there.
              </div>
              {matched.length === 0 && (
                <div style={{ fontSize: 11.5, color: '#bbb' }}>
                  No state-matched groups{st ? ` for ${STATE_NAMES[st] || st}` : ''}.
                </div>
              )}
              {matched.map(g => renderGroup(g, 'matched'))}
              {general.length > 0 && (
                <>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#c4c4c4', letterSpacing: '0.05em',
                    textTransform: 'uppercase', marginTop: 4 }}>General (no region)</div>
                  {general.map(g => renderGroup(g, 'general'))}
                </>
              )}
            </div>
          )}
        </div>

        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 6 }}>
          <ListingCopyButtons p={p} />
          <QueueAutoPostsButton p={p} groupNames={[...matched, ...general]}
            groupIdByName={groupIdByName} postedSet={postedSet} groupKey={groupKey} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontSize: 11, color: '#aaa' }}>
              {isNew ? `added ${relTime(p.firstSeenAt)}` : (p.firstSeenAt ? `seen ${relTime(p.firstSeenAt)}` : '')}
            </span>
            <a href={detailUrl}
              target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
              style={{ fontSize: 12, fontWeight: 600, color: '#6366f1', textDecoration: 'none' }}>
              View Details →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// 🤖 Queue this listing for automated posting across its matched groups.
// Sends only groups with a real numeric id (auto-poster navigates by id) that
// haven't been posted/queued yet. The cloud callable generates a DISTINCT caption
// variant per group and drip-schedules them (6/day max, ET business hours).
function QueueAutoPostsButton({ p, groupNames, groupIdByName, postedSet, groupKey }) {
  const [st, setSt] = useState({ s: 'idle', m: '' });
  const targets = useMemo(() => {
    return (groupNames || [])
      .filter(g => !postedSet.has(groupKey(g)))
      .map(g => ({ groupName: g, groupId: groupIdByName && groupIdByName[g] }))
      .filter(g => g.groupId && /^\d+$/.test(String(g.groupId)));
  }, [groupNames, groupIdByName, postedSet, groupKey]);

  async function run(e) {
    e.stopPropagation();
    if (!targets.length) { setSt({ s: 'error', m: 'no unposted groups with ids' }); setTimeout(() => setSt({ s: 'idle', m: '' }), 4000); return; }
    setSt({ s: 'loading', m: '' });
    try {
      const call = httpsCallable(functions, 'queueListingAutoPosts');
      const { data } = await call({ listing: p, groups: targets });
      const when = data.firstSlot ? new Date(data.firstSlot).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' }) : '';
      setSt({ s: 'done', m: `${data.queued} queued${when ? ` · first ${when}` : ''}` });
      setTimeout(() => setSt({ s: 'idle', m: '' }), 6000);
    } catch (err) {
      setSt({ s: 'error', m: (err.message || 'failed').slice(0, 38) });
      setTimeout(() => setSt({ s: 'idle', m: '' }), 6000);
    }
  }

  return (
    <button onClick={run} disabled={st.s === 'loading'}
      title={`Auto-post this listing to ${targets.length} group${targets.length !== 1 ? 's' : ''} on a slow drip (distinct AI caption per group; link in post for approval groups, link-in-comment otherwise)`}
      style={{ width: '100%', padding: '8px 12px', borderRadius: 9,
        border: `1px solid ${st.s === 'done' ? '#6ee7b7' : st.s === 'error' ? '#fecaca' : '#bfdbfe'}`,
        cursor: st.s === 'loading' ? 'default' : 'pointer', fontSize: 12, fontWeight: 700,
        background: st.s === 'done' ? '#ecfdf5' : st.s === 'error' ? '#fee2e2' : '#eff6ff',
        color: st.s === 'done' ? '#166534' : st.s === 'error' ? '#b91c1c' : '#1d4ed8',
        opacity: st.s === 'loading' ? 0.7 : 1 }}>
      {st.s === 'loading' ? '🤖 Queueing…' : st.s === 'done' ? `✓ ${st.m}` : st.s === 'error' ? `✕ ${st.m}` : `🤖 Queue Auto-Posts (${targets.length})`}
    </button>
  );
}

// Compact status board for the auto-post queue (newest 50).
function AutoPostQueuePanel() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(true);
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'autoPostQueue'), orderBy('scheduledFor', 'desc'), limit(50)),
      snap => setItems(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      e => console.error('autoPostQueue subscription error:', e)
    );
    return () => unsub();
  }, []);
  if (!items || !items.length) return null; // hidden until something is queued

  const CHIP = {
    queued:           { t: 'Queued',   bg: '#eff6ff', fg: '#1d4ed8' },
    posting:          { t: 'Posting…', bg: '#fef9c3', fg: '#a16207' },
    posted:           { t: 'Posted',   bg: '#ecfdf5', fg: '#166534' },
    pending_approval: { t: 'Pending approval', bg: '#f5f3ff', fg: '#6d28d9' },
    failed:           { t: 'Failed',   bg: '#fee2e2', fg: '#b91c1c' },
    skipped:          { t: 'Skipped',  bg: '#f3f4f6', fg: '#6b7280' },
  };
  const fmt = (ts) => {
    const d = ts && ts.toDate ? ts.toDate() : null;
    return d ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
  };
  const counts = items.reduce((m, i) => { m[i.status] = (m[i.status] || 0) + 1; return m; }, {});

  return (
    <div style={{ margin: '0 24px 14px', border: '1px solid #ececec', borderRadius: 12, background: '#fff' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
          background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: '#111' }}>🤖 Auto-Post Queue</span>
        <span style={{ fontSize: 11.5, color: '#888' }}>
          {['queued', 'posting', 'posted', 'pending_approval', 'failed']
            .filter(k => counts[k]).map(k => `${counts[k]} ${CHIP[k].t.toLowerCase()}`).join(' · ')}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#bbb' }}>{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div style={{ maxHeight: 240, overflowY: 'auto', borderTop: '1px solid #f3f3f3' }}>
          {items.map(i => {
            const c = CHIP[i.status] || CHIP.queued;
            return (
              <div key={i.id} title={i.error || ''}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 14px',
                  borderBottom: '1px solid #fafafa', fontSize: 12 }}>
                <span style={{ color: '#999', width: 108, flexShrink: 0 }}>{fmt(i.scheduledFor)}</span>
                <span style={{ fontWeight: 600, color: '#333', flex: 1, minWidth: 0, overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.groupName}</span>
                <span style={{ color: '#888', flex: 1, minWidth: 0, overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.listingAddr}</span>
                {i.status === 'posted' && (i.linkCommented
                  ? <span style={{ fontSize: 10.5, color: '#059669' }}>link ✓</span>
                  : (i.includeLink ? <span style={{ fontSize: 10.5, color: '#059669' }}>link in post</span>
                    : <span style={{ fontSize: 10.5, color: '#d97706' }}>link ✗</span>))}
                <span style={{ fontSize: 10.5, fontWeight: 700, padding: '3px 9px', borderRadius: 999,
                  background: c.bg, color: c.fg, flexShrink: 0 }}>{c.t}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RescrapeDealsButton({ onDone }) {
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [msg, setMsg] = useState('');
  async function run() {
    setStatus('loading'); setMsg('');
    try {
      const res = await fetch(`${MSG_SERVER}/express-deals-scrape`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.error || `HTTP ${res.status}`);
      setStatus('done');
      setMsg(`+${data.added ?? 0} new · ${data.scraped ?? 0} total`);
      onDone && onDone(data);
      setTimeout(() => setStatus('idle'), 4000);
    } catch (e) {
      setStatus('error'); setMsg(e.message || 'failed');
      setTimeout(() => setStatus('idle'), 5000);
    }
  }
  return (
    <button onClick={run} disabled={status === 'loading'}
      style={{ padding: '7px 14px', borderRadius: 8, border: 'none', cursor: status === 'loading' ? 'default' : 'pointer',
        fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap',
        background: status === 'error' ? '#fee2e2' : status === 'done' ? '#dcfce7' : 'linear-gradient(135deg,#6366f1,#8b5cf6)',
        color: status === 'error' ? '#b91c1c' : status === 'done' ? '#166534' : '#fff' }}>
      {status === 'loading' ? 'Scraping…' : status === 'done' ? `✓ ${msg}` : status === 'error' ? `✕ ${msg}` : '↻ Rescrape deals'}
    </button>
  );
}

// Embedded panel on the comment feed page: our wholesale properties, newest first,
// with a "NEW" highlight for anything added in the last NEW_WINDOW_HOURS.
function PropertiesPanel({ properties, loading, groups, groupIdByName, selectedPropertyId, onSelectProperty }) {
  const [collapsed, setCollapsed] = useState(false);
  const [newOnly, setNewOnly] = useState(false);
  const [showGone, setShowGone] = useState(false);

  const stats = useMemo(() => {
    const active = properties.filter(p => p.status !== 'removed');
    return {
      active: active.length,
      new: active.filter(isNewProperty).length,
      removed: properties.filter(p => p.status === 'removed').length,
    };
  }, [properties]);

  const list = useMemo(() => {
    return [...properties]
      .filter(p => (showGone ? true : p.status !== 'removed'))
      .filter(p => (newOnly ? isNewProperty(p) : true))
      .sort((a, b) => {
        const an = isNewProperty(a) ? 1 : 0, bn = isNewProperty(b) ? 1 : 0;
        if (an !== bn) return bn - an;
        const at = tsToMs(a.firstSeenAt) || 0, bt = tsToMs(b.firstSeenAt) || 0;
        if (at !== bt) return bt - at;
        return (b.wholesalePrice || 0) - (a.wholesalePrice || 0);
      });
  }, [properties, newOnly, showGone]);

  return (
    <div style={{ borderBottom: '1px solid #eee', background: '#fbfbfd' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 24px', flexWrap: 'wrap' }}>
        <button onClick={() => setCollapsed(c => !c)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#888', padding: 0 }}>
          {collapsed ? '▸' : '▾'}
        </button>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#111' }}>🏠 Our Properties</span>
        <span style={{ fontSize: 12, color: '#9aa' }}>Express Home Buyers</span>
        {stats.new > 0 && (
          <span style={{ background: '#ecfdf5', color: '#065f46', border: '1px solid #6ee7b7',
            fontSize: 11, fontWeight: 800, padding: '2px 9px', borderRadius: 999 }}>
            {stats.new} NEW
          </span>
        )}
        <span style={{ background: '#f3f4f6', color: '#666', fontSize: 11, fontWeight: 600,
          padding: '2px 9px', borderRadius: 999 }}>{stats.active} active</span>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#666', cursor: 'pointer' }}>
            <input type="checkbox" checked={newOnly} onChange={e => setNewOnly(e.target.checked)} /> New only
          </label>
          {stats.removed > 0 && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#666', cursor: 'pointer' }}>
              <input type="checkbox" checked={showGone} onChange={e => setShowGone(e.target.checked)} /> Removed ({stats.removed})
            </label>
          )}
          <RescrapeDealsButton />
        </div>
      </div>

      {/* Horizontal card strip */}
      {!collapsed && (
        loading ? (
          <div style={{ padding: '0 24px 16px', color: '#ccc', fontSize: 13 }}>Loading properties…</div>
        ) : list.length === 0 ? (
          <div style={{ padding: '0 24px 16px', color: '#bbb', fontSize: 13 }}>
            No properties yet — click <strong>Rescrape deals</strong>.
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 14, overflowX: 'auto', padding: '0 24px 18px', scrollbarWidth: 'thin' }}>
            {list.map(p => (
              <div key={p.id} style={{ flex: '0 0 250px', width: 250 }}>
                <PropertyCard p={p} groups={groups} groupIdByName={groupIdByName}
                  selected={p.id === selectedPropertyId} onSelect={onSelectProperty} />
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

// ─── Find Groups (area-based discovery) ──────────────────────────────────────
function DiscoveredGroupCard({ g }) {
  const [imgErr, setImgErr] = useState(false);
  const members = g.memberCount != null
    ? (g.memberCount >= 1000 ? `${(g.memberCount / 1000).toFixed(g.memberCount >= 10000 ? 0 : 1)}K` : g.memberCount) + ' members'
    : null;
  const privacy = g.isPublic ? 'Public' : g.isPrivate ? 'Private' : null;
  const dist = g.distanceMiles != null ? `${g.distanceMiles} mi` : null;
  // Badge respects the new distance-aware geoKind; falls back to the old boolean for legacy docs.
  let badge;
  if (g.geoKind === 'far') {
    badge = <span style={{ fontSize:10, color:'#999', background:'#f5f5f5', borderRadius:5, padding:'2px 6px' }}>{dist ? `${dist} away` : 'other area'}</span>;
  } else if (g.geoKind === 'nearby') {
    badge = <span style={{ fontSize:10, fontWeight:700, color:'#5b21b6', background:'#f3e8ff', borderRadius:5, padding:'2px 6px' }}>📍 {dist ? `${dist} away` : 'nearby'}</span>;
  } else if (g.geoKind === 'state') {
    badge = <span style={{ fontSize:10, fontWeight:700, color:'#5b21b6', background:'#f3e8ff', borderRadius:5, padding:'2px 6px' }}>📍 statewide</span>;
  } else if (g.geoKind === 'city' || (g.geoKind == null && g.geoMatch)) {
    badge = <span style={{ fontSize:10, fontWeight:700, color:'#5b21b6', background:'#f3e8ff', borderRadius:5, padding:'2px 6px' }}>📍 matches area</span>;
  } else {
    badge = <span style={{ fontSize:10, color:'#999', background:'#f5f5f5', borderRadius:5, padding:'2px 6px' }}>general</span>;
  }
  return (
    <a href={g.url} target="_blank" rel="noreferrer" style={{
      display:'flex', gap:10, padding:10, border:'1px solid #ececec', borderRadius:10,
      textDecoration:'none', color:'inherit', background:'#fff', alignItems:'flex-start',
    }}>
      <div style={{ width:46, height:46, borderRadius:10, flexShrink:0, overflow:'hidden',
        background:'#f0f0f0', display:'flex', alignItems:'center', justifyContent:'center' }}>
        {g.imageUrl && !imgErr
          ? <img src={g.imageUrl} alt="" referrerPolicy="no-referrer" loading="lazy" onError={() => setImgErr(true)} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
          : <span style={{ fontSize:18 }}>👥</span>}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:13, fontWeight:600, color:'#111', lineHeight:1.25,
          overflow:'hidden', textOverflow:'ellipsis', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
          {g.name}
        </div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:5, alignItems:'center' }}>
          {badge}
          {privacy && <span style={{ fontSize:11, color:'#888' }}>{privacy}</span>}
          {members && <span style={{ fontSize:11, color:'#888' }}>· {members}</span>}
          {g.isJoined && <span style={{ fontSize:10, color:'#059669' }}>· joined</span>}
        </div>
      </div>
    </a>
  );
}

function FindGroupsModal({ onClose }) {
  const [city, setCity]     = useState('');
  const [stateV, setStateV] = useState('');
  const [zip, setZip]       = useState('');
  const [radius, setRadius] = useState(50); // "matches area" radius in miles
  const [runId, setRunId]   = useState(null);
  const [running, setRunning] = useState(false);
  const [lines, setLines]   = useState([]);
  const [results, setResults] = useState([]);
  const [err, setErr]       = useState('');
  const [showLog, setShowLog] = useState(false);
  const esRef = useRef(null);
  const logRef = useRef(null);

  // Live results for this run from Firestore
  useEffect(() => {
    if (!runId) return;
    const unsub = onSnapshot(
      query(collection(db, 'fbDiscoveredGroups'), where('searchRunId', '==', runId)),
      snap => setResults(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    );
    return () => unsub();
  }, [runId]);

  // Live log via SSE
  useEffect(() => {
    if (!runId) return;
    const es = new EventSource(`${MSG_SERVER}/discover-stream`);
    esRef.current = es;
    es.onmessage = (e) => {
      let msg; try { msg = JSON.parse(e.data); } catch { return; }
      if (msg.type === 'snapshot') { setLines(msg.lines || []); setRunning(msg.running); }
      else if (msg.type === 'line') setLines(prev => [...prev, msg.text]);
      else if (msg.type === 'status' && msg.running === false) { setRunning(false); try { es.close(); } catch {} }
    };
    es.onerror = () => {};
    return () => { try { es.close(); } catch {} };
  }, [runId]);

  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [lines]);

  async function start() {
    setErr('');
    if (!city.trim() && !stateV.trim() && !zip.trim()) { setErr('Enter a city, state, or zip.'); return; }
    try {
      const res = await fetch(`${MSG_SERVER}/discover-groups`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ city: city.trim(), state: stateV.trim(), zip: zip.trim(), radius }),
      });
      const data = await res.json();
      if (!res.ok) { setErr(data.error || 'Failed to start'); return; }
      setResults([]); setLines(data.lines || []); setRunId(data.runId); setRunning(true); setShowLog(true);
    } catch {
      setErr(`Local server offline — start messageServer on :3002 (${MSG_SERVER})`);
    }
  }
  async function stop() {
    try { await fetch(`${MSG_SERVER}/discover-stop`, { method: 'POST' }); } catch {}
    setRunning(false);
  }

  // Group results into ordered tiers: matches area → statewide → nearby (miles) → general.
  const { sections, total, geoCount } = useMemo(() => {
    const b = { matches: [], statewide: [], miles: [], general: [] };
    for (const g of results) {
      const k = g.geoKind;
      if (k === 'city' || (k == null && g.geoMatch)) b.matches.push(g);   // names your city (legacy match → here)
      else if (k === 'state') b.statewide.push(g);                        // whole-state group
      else if (k === 'nearby' || k === 'far') b.miles.push(g);            // names another city, ranked by distance
      else b.general.push(g);                                             // no geographic signal
    }
    const byMembers = (a, c) => (c.memberCount || 0) - (a.memberCount || 0);
    b.matches.sort(byMembers);
    b.statewide.sort(byMembers);
    b.general.sort(byMembers);
    b.miles.sort((a, c) => {
      const ad = a.distanceMiles == null ? Infinity : a.distanceMiles;
      const cd = c.distanceMiles == null ? Infinity : c.distanceMiles;
      return ad !== cd ? ad - cd : byMembers(a, c);                        // nearest first
    });
    const sections = [
      { key: 'matches',   label: '📍 Matches area', hint: 'names your city', items: b.matches },
      { key: 'statewide', label: '🗺️ Statewide',    hint: 'whole state',     items: b.statewide },
      { key: 'miles',     label: '🚗 Miles away',   hint: 'nearest first',   items: b.miles },
      { key: 'general',   label: 'General',         hint: 'no area match',   items: b.general },
    ].filter(s => s.items.length);
    return { sections, total: results.length, geoCount: results.filter(g => g.geoMatch).length };
  }, [results]);

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div onClick={e => e.stopPropagation()} style={{ width:'min(820px, 96vw)', maxHeight:'90vh',
        background:'#fff', borderRadius:14, boxShadow:'0 24px 70px rgba(0,0,0,0.3)', display:'flex',
        flexDirection:'column', overflow:'hidden' }}>

        {/* Header */}
        <div style={{ padding:'16px 20px', borderBottom:'1px solid #f0f0f0', display:'flex',
          alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:'#111' }}>🔎 Find Facebook Groups</div>
            <div style={{ fontSize:12, color:'#999', marginTop:2 }}>
              Search a city, state, or zip for real-estate &amp; investor groups
            </div>
          </div>
          <button onClick={onClose} style={{ border:'none', background:'none', fontSize:22, color:'#bbb', cursor:'pointer', lineHeight:1 }}>×</button>
        </div>

        {/* Inputs */}
        <div style={{ padding:'14px 20px', borderBottom:'1px solid #f5f5f5', display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <input placeholder="City" value={city} onChange={e => setCity(e.target.value)} disabled={running}
            style={{ flex:'2 1 160px', padding:'8px 11px', border:'1px solid #e3e3e3', borderRadius:8, fontSize:13, outline:'none' }} />
          <input placeholder="State (e.g. VA)" value={stateV} onChange={e => setStateV(e.target.value)} disabled={running}
            style={{ flex:'1 1 110px', padding:'8px 11px', border:'1px solid #e3e3e3', borderRadius:8, fontSize:13, outline:'none' }} />
          <input placeholder="Zip" value={zip} onChange={e => setZip(e.target.value)} disabled={running}
            style={{ flex:'1 1 90px', padding:'8px 11px', border:'1px solid #e3e3e3', borderRadius:8, fontSize:13, outline:'none' }} />
          {running ? (
            <button onClick={stop} style={{ padding:'8px 16px', borderRadius:8, border:'none', cursor:'pointer',
              fontSize:13, fontWeight:600, background:'#fee2e2', color:'#b91c1c' }}>■ Stop</button>
          ) : (
            <button onClick={start} style={{ padding:'8px 18px', borderRadius:8, border:'none', cursor:'pointer',
              fontSize:13, fontWeight:600, background:'#111', color:'#fff' }}>Find Groups</button>
          )}
        </div>

        {/* Match-radius dial — groups naming a city within this many miles count as "matches area" */}
        <div style={{ padding:'11px 20px', borderBottom:'1px solid #f5f5f5', display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:12, color:'#666', fontWeight:600, whiteSpace:'nowrap' }}>Match radius</span>
          <input type="range" min={10} max={250} step={5} value={radius} disabled={running}
            onChange={e => setRadius(Number(e.target.value))}
            style={{ flex:1, accentColor:'#5b21b6', cursor: running ? 'default' : 'pointer' }} />
          <span style={{ fontSize:12, fontWeight:700, color:'#5b21b6', minWidth:52, textAlign:'right' }}>{radius} mi</span>
        </div>

        {err && <div style={{ padding:'8px 20px', fontSize:12, color:'#b91c1c', background:'#fef2f2' }}>{err}</div>}

        {/* Results */}
        <div style={{ flex:1, overflowY:'auto', padding:'14px 20px' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
            <span style={{ fontSize:12, color:'#888' }}>
              {runId ? `${total} group${total !== 1 ? 's' : ''} found · ${geoCount} match area` : 'Results will appear here as they’re found.'}
              {running && <span style={{ color:'#5b21b6' }}> · searching…</span>}
            </span>
            {lines.length > 0 && (
              <button onClick={() => setShowLog(s => !s)} style={{ border:'none', background:'none', cursor:'pointer', fontSize:11, color:'#999' }}>
                {showLog ? 'Hide log' : 'Show log'}
              </button>
            )}
          </div>

          {total === 0 && !running && runId && (
            <div style={{ padding:'30px 0', textAlign:'center', color:'#ccc', fontSize:13 }}>No groups found for this area.</div>
          )}

          {sections.map(sec => (
            <div key={sec.key} style={{ marginBottom:16 }}>
              <div style={{ display:'flex', alignItems:'baseline', gap:8, margin:'2px 0 8px', position:'sticky', top:0,
                background:'#fff', paddingBottom:2 }}>
                <span style={{ fontSize:12, fontWeight:700, color:'#444' }}>{sec.label}</span>
                <span style={{ fontSize:11, color:'#bbb' }}>{sec.items.length} · {sec.hint}</span>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(230px, 1fr))', gap:10 }}>
                {sec.items.map(g => <DiscoveredGroupCard key={g.id} g={g} />)}
              </div>
            </div>
          ))}
        </div>

        {/* Log */}
        {showLog && (
          <div ref={logRef} style={{ height:130, overflowY:'auto', background:'#0d1117', color:'#9fe88d',
            fontFamily:'ui-monospace, monospace', fontSize:11, padding:'8px 14px', borderTop:'1px solid #222', whiteSpace:'pre-wrap' }}>
            {lines.length ? lines.map((l, i) => <div key={i}>{l}</div>) : <div style={{ color:'#555' }}>No output yet…</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function FindGroupsButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} style={{ padding:'6px 13px', borderRadius:8, border:'1px solid #e5e5e5',
        background:'#fff', color:'#444', fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>
        🔎 Find Groups
      </button>
      {open && <FindGroupsModal onClose={() => setOpen(false)} />}
    </>
  );
}

// ── Scrape Health panel ───────────────────────────────────────────────────────
// Mirrors the /nd-seller-leads health panel: shows whether the daily FB comments
// scrape ran and how many comments it pulled, with full run history from
// fbCommentScrapeRuns. Statuses: success (all groups ok), partial (some groups
// failed but comments saved), failed (nothing worked), running.
const FB_HEALTH_STATUS = {
  success: { bg: '#dcfce7', fg: '#15803d', dot: '#16a34a', label: 'OK' },
  partial: { bg: '#fef3c7', fg: '#b45309', dot: '#d97706', label: 'Partial' },
  failed:  { bg: '#fee2e2', fg: '#b91c1c', dot: '#dc2626', label: 'Failed' },
  running: { bg: '#dbeafe', fg: '#1d4ed8', dot: '#2563eb', label: 'Running' },
};
function fmtRunTime(ts) {
  try {
    const d = ts && ts.toDate ? ts.toDate() : (ts ? new Date(ts) : null);
    return d ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
  } catch { return '—'; }
}
const fbRunMs = (r) => (r.startedAt && r.startedAt.toMillis ? r.startedAt.toMillis() : (r.startedAt ? new Date(r.startedAt).getTime() : 0));

function FbScrapeHealthPanel({ runs }) {
  const [open, setOpen] = useState(false);
  const th = { padding: '6px 8px', fontWeight: 600, position: 'sticky', top: 0, background: '#fff', whiteSpace: 'nowrap' };
  const td = { padding: '6px 8px', verticalAlign: 'top', borderTop: '1px solid #efefef' };

  const sorted = [...runs].sort((a, b) => fbRunMs(b) - fbRunMs(a));
  const weekAgo = Date.now() - 7 * 864e5;
  const recent = sorted.filter(r => fbRunMs(r) >= weekAgo);
  const c = recent.reduce((a, r) => (a[r.status] = (a[r.status] || 0) + 1, a), {});
  const comments7 = recent.reduce((a, r) => a + (r.commentsScraped || 0), 0);
  const todayKey = new Date().toISOString().slice(0, 10);
  const todayRun = sorted.find(r => r.dateKey === todayKey);

  const todayPill = (() => {
    const s = todayRun ? (FB_HEALTH_STATUS[todayRun.status] || FB_HEALTH_STATUS.failed)
                       : { bg: '#f1f5f9', fg: '#94a3b8', dot: '#cbd5e1', label: 'No run yet' };
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 600, background: s.bg, color: s.fg }}>
        <span style={{ width: 7, height: 7, borderRadius: 999, background: s.dot }} />
        Today · {todayRun ? `${s.label}${todayRun.commentsScraped != null ? ` · ${todayRun.commentsScraped} comments` : ''}` : 'No run yet'}
      </span>
    );
  })();

  return (
    <div style={{ borderBottom: '1px solid #efefef', background: '#fff' }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 24px', cursor: 'pointer', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#888' }}>Scrape Health</span>
        {todayPill}
        <span style={{ fontSize: 12, color: '#aaa' }}>
          7d: <b style={{ color: '#15803d' }}>{c.success || 0}</b> ok · <b style={{ color: '#b45309' }}>{c.partial || 0}</b> partial · <b style={{ color: '#b91c1c' }}>{c.failed || 0}</b> failed · {comments7} comments
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#888' }}>{open ? '▾' : '▸'} {sorted.length} runs</span>
      </div>
      {open && (
        <div style={{ maxHeight: 300, overflowY: 'auto', padding: '0 24px 14px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: '#333' }}>
            <thead>
              <tr style={{ color: '#aaa', textAlign: 'left' }}>
                <th style={th}>Started</th>
                <th style={th}>Status</th>
                <th style={{ ...th, textAlign: 'right' }}>Groups</th>
                <th style={{ ...th, textAlign: 'right' }}>Comments</th>
                <th style={th}>Source</th>
                <th style={th}>Note</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => {
                const s = FB_HEALTH_STATUS[r.status] || FB_HEALTH_STATUS.failed;
                const groupsLabel = r.groupsTotal != null
                  ? `${r.groupsDone != null ? r.groupsDone : '—'}/${r.groupsTotal}${r.groupsFailed ? ` (${r.groupsFailed} failed)` : ''}`
                  : '—';
                return (
                  <tr key={r.id}>
                    <td style={td}>{fmtRunTime(r.startedAt)}</td>
                    <td style={td}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 999, background: s.bg, color: s.fg, fontWeight: 600, fontSize: 11 }}>
                        <span style={{ width: 6, height: 6, borderRadius: 999, background: s.dot }} />{s.label}
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: 'right', color: '#666' }}>{groupsLabel}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: r.commentsScraped > 0 ? '#111' : '#aaa' }}>{r.commentsScraped != null ? r.commentsScraped : '—'}</td>
                    <td style={{ ...td, color: '#888' }}>{r.source || '—'}</td>
                    <td style={{ ...td, color: '#aaa', maxWidth: 320 }}>{r.error || ''}</td>
                  </tr>
                );
              })}
              {sorted.length === 0 && (
                <tr><td colSpan={6} style={{ ...td, color: '#aaa', padding: '16px 8px' }}>No scrape runs recorded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function FbGroupCommentsDashboard() {
  const [comments, setComments]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  const [search, setSearch]             = useState('');
  const [preset, setPreset]             = useState('all');
  const [customRange, setCustomRange]   = useState({ from: null, to: null });
  const [calOpen, setCalOpen]           = useState(false);
  const [intentFilter, setIntentFilter] = useState('all');
  const [sortMode, setSortMode]         = useState('intent'); // 'intent' | 'recent'
  const [aiScores, setAiScores]         = useState({}); // id -> { tier, reason }
  const [properties, setProperties]     = useState([]);
  const [propsLoading, setPropsLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [scrapeRuns, setScrapeRuns]     = useState([]);
  const calRef = useRef(null);

  useEffect(() => {
    function down(e) { if (calRef.current && !calRef.current.contains(e.target)) setCalOpen(false); }
    document.addEventListener('mousedown', down);
    return () => document.removeEventListener('mousedown', down);
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'fbGroupComments')), snap => {
      setComments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Express Home Buyers wholesale deals (our properties)
  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'expressHomeDeals')), snap => {
      setProperties(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setPropsLoading(false);
    });
    return () => unsub();
  }, []);

  // Scrape run-health records (powers the Scrape Health panel).
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'fbCommentScrapeRuns'), orderBy('startedAt', 'desc'), limit(120)),
      snap => setScrapeRuns(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      e => console.error('fbCommentScrapeRuns subscription error:', e)
    );
    return () => unsub();
  }, []);


  const groupCounts = useMemo(() => {
    const m = {};
    comments.forEach(c => { if (c.groupName) m[c.groupName] = (m[c.groupName] || 0) + 1; });
    return m;
  }, [comments]);

  const groups = useMemo(() => Object.keys(groupCounts).sort(), [groupCounts]);

  // Map group name → FB groupId (harvested from scraped comments) so property cards
  // can build real facebook.com/groups/{id} links. Falls back to the name when the
  // id isn't known yet.
  const groupIdByName = useMemo(() => {
    const m = {};
    comments.forEach(c => { if (c.groupName && c.groupId && !m[c.groupName]) m[c.groupName] = c.groupId; });
    return m;
  }, [comments]);

  function toggleGroup(g) {
    setSelectedProperty(null); // sidebar group selection and property filter are mutually exclusive
    setSelectedGroups(prev => {
      const next = new Set(prev);
      next.has(g) ? next.delete(g) : next.add(g);
      return next;
    });
  }
  function clearGroups() { setSelectedGroups(new Set()); }
  function selectAll()   { setSelectedProperty(null); setSelectedGroups(new Set(groups)); }

  // Clicking a property card filters the feed to that property's groups; clicking it
  // again (or any sidebar group) clears it. Property + sidebar selection are exclusive.
  function selectProperty(p) {
    setSelectedProperty(prev => {
      const next = prev && p && prev.id === p.id ? null : p;
      if (next) setSelectedGroups(new Set());
      return next;
    });
  }

  // The set of group names a selected property can post into: state-matched + region-less.
  const propertyFilter = useMemo(() => {
    if (!selectedProperty) return null;
    const { matched, general, state } = groupsForProperty(groups, selectedProperty);
    return { set: new Set([...matched, ...general]), matched, general, state };
  }, [selectedProperty, groups]);

  // Person-level DM tracking: a person counts as DMed if ANY of their comments
  // (across all groups) is flagged. Drives the highlight + button on every card.
  const dmedKeys = useMemo(() => {
    const s = new Set();
    comments.forEach(c => { if (c.dmed) s.add(personKey(c)); });
    return s;
  }, [comments]);

  // Toggle DMed for everyone matching this person's key. Persists to Firestore;
  // the onSnapshot listener re-renders the highlight automatically.
  async function toggleDmed(entry) {
    const key = personKey(entry);
    const isDmed = dmedKeys.has(key);
    const targets = isDmed
      ? comments.filter(c => c.dmed && personKey(c) === key)   // unmark every flagged comment
      : [entry];                                               // mark just the clicked one
    await Promise.all(targets.map(c =>
      updateDoc(doc(db, 'fbGroupComments', c.id),
        isDmed ? { dmed: false, dmedAt: null } : { dmed: true, dmedAt: serverTimestamp() })
    ));
  }

  const dateRange = useMemo(() => {
    if (preset === 'custom') return customRange;
    return getPresetRange(preset);
  }, [preset, customRange]);

  // Attach intent to every comment (AI score overrides regex if available)
  const withIntent = useMemo(() => {
    return comments.map(c => {
      const ai = aiScores[c.id];
      if (ai) {
        const tier = ai.tier;
        const map = {
          offer: { tier:'offer', score:4, label:'💰 Offer', color:'#065f46', bg:'#ecfdf5', border:'#6ee7b7' },
          hot:   { tier:'hot',   score:3, label:'🔥 Hot',   color:'#dc2626', bg:'#fff5f5', border:'#fecaca' },
          warm:  { tier:'warm',  score:2, label:'👍 Warm',  color:'#b45309', bg:'#fffbeb', border:'#fde68a' },
          noise: { tier:'noise', score:0, label:'📋 Noise', color:'#9ca3af', bg:'#f9fafb', border:'#e5e7eb' },
        };
        return { ...c, intent: map[tier] || scoreIntent(c.comment), aiReason: ai.reason, isAI: true };
      }
      return { ...c, intent: scoreIntent(c.comment), aiReason: null, isAI: false };
    });
  }, [comments, aiScores]);

  const filtered = useMemo(() => {
    return withIntent
      .filter(c => {
        if (propertyFilter && !propertyFilter.set.has(c.groupName)) return false;
        if (selectedGroups.size > 0 && !selectedGroups.has(c.groupName)) return false;
        const q = search.toLowerCase();
        if (q && !c.name?.toLowerCase().includes(q) && !c.comment?.toLowerCase().includes(q)) return false;
        if (dateRange?.from || dateRange?.to) {
          const d = parseDate(c.commentTime);
          if (!d) return false;
          if (dateRange.from && d < dateRange.from) return false;
          if (dateRange.to   && d > dateRange.to)   return false;
        }
        if (intentFilter !== 'all' && c.intent.tier !== intentFilter) return false;
        return true;
      })
      .sort((a, b) => {
        // When filtering by a property, the user asked for strict recency ordering.
        if (sortMode === 'intent' && !propertyFilter) {
          const diff = b.intent.score - a.intent.score;
          if (diff !== 0) return diff;
        }
        return (parseDate(b.commentTime) || 0) - (parseDate(a.commentTime) || 0);
      });
  }, [withIntent, propertyFilter, selectedGroups, search, dateRange, intentFilter, sortMode]);

  // Count per tier for chips (before intent filter)
  const tierCounts = useMemo(() => {
    const base = withIntent.filter(c => {
      if (propertyFilter && !propertyFilter.set.has(c.groupName)) return false;
      if (selectedGroups.size > 0 && !selectedGroups.has(c.groupName)) return false;
      const q = search.toLowerCase();
      if (q && !c.name?.toLowerCase().includes(q) && !c.comment?.toLowerCase().includes(q)) return false;
      if (dateRange?.from || dateRange?.to) {
        const d = parseDate(c.commentTime);
        if (!d) return false;
        if (dateRange.from && d < dateRange.from) return false;
        if (dateRange.to   && d > dateRange.to)   return false;
      }
      return true;
    });
    const m = { all: base.length, offer: 0, hot: 0, warm: 0, noise: 0 };
    base.forEach(c => { if (m[c.intent.tier] !== undefined) m[c.intent.tier]++; });
    return m;
  }, [withIntent, propertyFilter, selectedGroups, search, dateRange]);

  function handleAIResults(results) {
    setAiScores(prev => {
      const next = { ...prev };
      results.forEach(r => { next[r.id] = { tier: r.tier, reason: r.reason }; });
      return next;
    });
  }

  const customLabel = useMemo(() => {
    if (!customRange.from) return 'Custom';
    const f = customRange.from.toLocaleDateString('en-US', { month:'short', day:'numeric' });
    if (!customRange.to) return `${f} →`;
    const t = customRange.to.toLocaleDateString('en-US', { month:'short', day:'numeric' });
    return `${f} – ${t}`;
  }, [customRange]);

  const aiScoredCount = Object.keys(aiScores).length;

  return (
    <div style={{ display:'flex', flexDirection:'column', minHeight:'100vh', background:'#fff',
      fontFamily:'-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

      {/* Top run-scrape bar — full width */}
      <RunScrapeBar />

      {/* Daily scrape health — full width, mirrors /nd-seller-leads */}
      <FbScrapeHealthPanel runs={scrapeRuns} />

      <div style={{ display:'flex', flex:1, minHeight:0 }}>

      {/* ── Sidebar ── */}
      <div style={{ width:260, flexShrink:0, borderRight:'1px solid #efefef',
        background:'#fafafa', display:'flex', flexDirection:'column', minHeight:'100vh' }}>

        {/* Brand */}
        <div style={{ padding:'24px 20px 16px', borderBottom:'1px solid #efefef' }}>
          <div style={{ fontSize:15, fontWeight:700, color:'#111', letterSpacing:'-0.3px' }}>Comment Feed</div>
          <div style={{ fontSize:12, color:'#aaa', marginTop:2 }}>
            {loading ? '—' : `${comments.length} total · ${tierCounts.offer} offers · ${tierCounts.hot} hot`}
          </div>
          {aiScoredCount > 0 && (
            <div style={{ fontSize:11, color:'#8b5cf6', marginTop:4, display:'flex', alignItems:'center', gap:4 }}>
              <span>✦</span> {aiScoredCount} AI-scored
            </div>
          )}
        </div>

        {/* Date filters */}
        <div style={{ padding:'16px 12px 8px' }}>
          <div style={{ fontSize:10, fontWeight:700, color:'#bbb', letterSpacing:'0.08em',
            textTransform:'uppercase', padding:'0 4px', marginBottom:6 }}>Time Range</div>
          {PRESETS.map(p => {
            const active = preset === p.key;
            return (
              <div key={p.key} ref={p.key === 'custom' ? calRef : null} style={{ position:'relative' }}>
                <button
                  onClick={() => {
                    setPreset(p.key);
                    if (p.key === 'custom') setCalOpen(o => !o);
                    else setCalOpen(false);
                  }}
                  style={{
                    width:'100%', textAlign:'left', padding:'7px 12px', borderRadius:6,
                    border:'none', cursor:'pointer', fontSize:13, fontWeight: active ? 600 : 400,
                    background: active ? '#111' : 'transparent',
                    color: active ? '#fff' : '#555',
                    display:'flex', justifyContent:'space-between', alignItems:'center',
                  }}
                >
                  <span>{p.key === 'custom' ? customLabel : p.label}</span>
                  {p.key === 'custom' && <span style={{ fontSize:10, opacity:0.6 }}>▾</span>}
                </button>
                {p.key === 'custom' && calOpen && (
                  <div style={{ position:'absolute', left:'calc(100% + 8px)', top:0, zIndex:200,
                    background:'#fff', border:'1px solid #e8e8e8', borderRadius:10, padding:16,
                    boxShadow:'0 12px 40px rgba(0,0,0,0.12)', width:236 }}>
                    <Calendar from={customRange.from} to={customRange.to}
                      onChange={r => { setCustomRange(r); if (r.from && r.to) setCalOpen(false); }} />
                    {(customRange.from || customRange.to) && (
                      <button onClick={() => { setCustomRange({from:null,to:null}); setPreset('all'); setCalOpen(false); }}
                        style={{ marginTop:10, width:'100%', padding:'6px 0', fontSize:12, color:'#888',
                          background:'none', border:'1px solid #e5e5e5', borderRadius:6, cursor:'pointer' }}>
                        Clear dates
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Group filters */}
        <div style={{ padding:'12px 12px 0', flex:1, overflowY:'auto' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
            padding:'0 4px', marginBottom:6 }}>
            <span style={{ fontSize:10, fontWeight:700, color:'#bbb', letterSpacing:'0.08em', textTransform:'uppercase' }}>
              Groups
            </span>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={selectAll} style={sidebarTextBtn}>All</button>
              <button onClick={clearGroups} style={sidebarTextBtn}>None</button>
            </div>
          </div>
          {loading ? (
            <div style={{ padding:'12px', fontSize:12, color:'#ccc' }}>Loading…</div>
          ) : groups.map(g => (
            <GroupItem key={g} name={g} count={groupCounts[g] || 0}
              checked={selectedGroups.has(g)} onChange={() => toggleGroup(g)} />
          ))}
        </div>

        {selectedGroups.size > 0 && (
          <div style={{ padding:'12px 16px', borderTop:'1px solid #efefef' }}>
            <span style={{ fontSize:12, color:'#999' }}>{selectedGroups.size} group{selectedGroups.size > 1 ? 's' : ''} selected</span>
          </div>
        )}
      </div>

      {/* ── Main content ── */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0 }}>

        {/* Our wholesale properties — embedded panel above the comment feed */}
        <PropertiesPanel properties={properties} loading={propsLoading} groups={groups}
          groupIdByName={groupIdByName}
          selectedPropertyId={selectedProperty?.id} onSelectProperty={selectProperty} />

        {/* Auto-post queue status (hidden until something is queued) */}
        <AutoPostQueuePanel />

        {/* Topbar */}
        <div style={{ padding:'14px 24px', borderBottom:'1px solid #f3f3f3', display:'flex',
          flexDirection:'column', gap:10 }}>

          {/* Row 1: search + sort + AI + count */}
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <input
              placeholder="Search comments or people…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex:1, maxWidth:360, padding:'7px 13px', border:'1px solid #e8e8e8',
                borderRadius:8, fontSize:13, color:'#111', background:'#fff', outline:'none' }}
            />

            {/* Sort toggle */}
            <div style={{ display:'flex', borderRadius:7, border:'1px solid #e5e5e5', overflow:'hidden', flexShrink:0 }}>
              {[['intent','By Intent'],['recent','Recent']].map(([key, label]) => (
                <button key={key} onClick={() => setSortMode(key)} style={{
                  padding:'6px 12px', fontSize:12, fontWeight: sortMode===key ? 700 : 400,
                  background: sortMode===key ? '#111' : '#fff',
                  color: sortMode===key ? '#fff' : '#777',
                  border:'none', cursor:'pointer', whiteSpace:'nowrap',
                }}>
                  {label}
                </button>
              ))}
            </div>

            <AIAnalyzeButton comments={filtered} onResults={handleAIResults} />

            <FindGroupsButton />

            <span style={{ fontSize:13, color:'#bbb', whiteSpace:'nowrap', flexShrink:0 }}>
              {loading ? '—' : `${filtered.length} result${filtered.length !== 1 ? 's' : ''}`}
            </span>
          </div>

          {/* Row 2: intent chips */}
          <IntentChips value={intentFilter} onChange={setIntentFilter} counts={tierCounts} />
        </div>

        {/* Feed */}
        <div style={{ flex:1, overflowY:'auto' }}>
          {/* Property filter banner */}
          {selectedProperty && (
            <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap',
              padding:'10px 24px', background:'#f5f3ff', borderBottom:'1px solid #ede9fe' }}>
              <span style={{ fontSize:13, color:'#4c1d95', fontWeight:600 }}>
                🏠 Filtering to <strong>{selectedProperty.streetAddress || selectedProperty.name}</strong>
                {propertyFilter?.state ? ` · ${STATE_NAMES[propertyFilter.state] || propertyFilter.state}` : ''}
                {' '}— {propertyFilter?.set.size || 0} group{(propertyFilter?.set.size || 0) !== 1 ? 's' : ''}, by recency
              </span>
              <button onClick={() => setSelectedProperty(null)}
                style={{ marginLeft:'auto', background:'#6366f1', color:'#fff', border:'none',
                  borderRadius:999, padding:'4px 12px', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                Clear ×
              </button>
            </div>
          )}
          {loading ? (
            <div style={{ padding:'80px 0', textAlign:'center', color:'#ddd', fontSize:14 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding:'80px 0', textAlign:'center', color:'#ddd', fontSize:14 }}>
              {selectedProperty
                ? `No comments in this property’s ${propertyFilter?.set.size || 0} group${(propertyFilter?.set.size || 0) !== 1 ? 's' : ''} match your filters.`
                : 'No comments match your filters.'}
            </div>
          ) : filtered.map(entry => (
            <CommentCard key={entry.id} entry={entry}
              intent={entry.intent} aiReason={entry.aiReason} isAI={entry.isAI}
              personDmed={dmedKeys.has(personKey(entry))} onToggleDmed={toggleDmed} />
          ))}
        </div>
      </div>

      </div>{/* close inner flex (sidebar + main) */}
    </div>
  );
}

const sidebarTextBtn = {
  background:'none', border:'none', cursor:'pointer',
  fontSize:11, color:'#bbb', padding:'1px 4px', borderRadius:4,
};
